import { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import Add from '@mui/icons-material/Add';
import { Box } from '@mui/material';
import DataTable, {
  DataTableWrapper,
  DataTableWrapperActionButton,
  getActionsColConfig,
  useTableQueryParams,
} from 'src/components/DataTable';
import { useUserRoles } from 'src/auth';
import { useModal } from 'src/hooks';
import { isModalComplete } from 'src/components/ModalDialog';
import DataTableContext from 'src/components/DataTable/components/DataTableContext';
import DataTableSearchBar from 'src/components/DataTable/components/DataTableSearchBar';
import { AlertType, useAlert } from 'src/components/AlertsProvider';
import { useStoreUnitActions } from 'src/redux/helpers';
import { pepApiRequest, RequestMethod, useStatefulApi } from 'src/services';
import { generateApiPath } from 'src/utils';
import solutionsStore, { useSolutionRuleConfigurationList } from '../redux';
import {
  SolutionRuleConfigurationField,
  SolutionRuleConfigurationItem,
} from '../types/solutionRulesConfiguration';
import SolutionRuleConfigurationCreateContainer from './components/SolutionRuleConfigurationCreateContainer';
import SolutionRuleConfigurationListAction from './components/SolutionRuleConfigurationListAction';
import SolutionAlertRuleStatus from '../SolutionAlertRules/components/SolutionAlertRuleStatus';
import SolutionRuleConfigurationDeleteModal from './components/SolutionRuleConfigurationDeleteModal';
import { API_ENABLE_RULE_CONFIGURATION } from '../constants';
import MoreActionsMenu from './components/MoreActionsMenu';

const columns = [
  {
    id: SolutionRuleConfigurationField.Name,
    title: 'Rule Name',
  },
  {
    id: SolutionRuleConfigurationField.Enabled,
    title: 'Enabled',
    Component: SolutionAlertRuleStatus,
  },
  {
    id: SolutionRuleConfigurationField.TargetUrl,
    title: 'Target URL',
  },
  getActionsColConfig(SolutionRuleConfigurationListAction),
];

export default function SolutionRuleConfiguration() {
  const { showAlert } = useAlert();
  const [data, loading] = useSolutionRuleConfigurationList();
  const [item, setSelectedItems] = useState<any[]>([]);
  const { paging, search, sorting } = useTableQueryParams();
  const { solutionId } = useParams();
  const { isPlatformReader } = useUserRoles();
  const actions = useStoreUnitActions(solutionsStore);
  const [canEnable, setCanEnable] = useState(false);
  const [canDisable, setCanDisable] = useState(false);
  const itemEnable = item?.map((x) => x.Enabled);
  const itemIds = item?.map((x) => x.id);

  useEffect(() => {
    actions.sites(solutionId);
  }, [solutionId]);

  useEffect(() => {
    setCanEnable(!item?.some((items) => items.enabled === false));
    setCanDisable(!item?.some((items) => items.enabled === true));
  }, [itemEnable]);

  useEffect(() => {
    actions.ruleConfiguration(solutionId);
  }, []);

  const [CreateNewRuleConfigurationDialog, showNewRuleConfigurationDialog] =
    useModal(SolutionRuleConfigurationCreateContainer, {
      onClose: (event, reason) =>
        isModalComplete(reason) && actions.ruleConfiguration(solutionId),
    });

  function onSelectItems(selectedItems) {
    const items = Object.keys(selectedItems).filter((k) => selectedItems[k]);
    const selectedRuleItems: SolutionRuleConfigurationItem[] = [];

    items?.forEach((item) => {
      const selectedItem = data?.find((dataItem) => dataItem.id === item);
      if (selectedItem) {
        selectedRuleItems.push(selectedItem);
      }
    });
    setSelectedItems(selectedRuleItems);
  }

  const [DeleteDialog, showDeleteDialog] = useModal(
    SolutionRuleConfigurationDeleteModal,
    { itemIds }
  );

  const [handleEnableClick] = useStatefulApi(
    () =>
      pepApiRequest({
        method: RequestMethod.Put,
        url: generateApiPath(API_ENABLE_RULE_CONFIGURATION, { solutionId }),
        params: { enabled: true, ruleIds: itemIds },
      }),
    () => {
      showAlert({
        type: AlertType.Info,
        title: 'Rule is Enabled',
        text: 'Rule has been enabled successfully',
      });
      actions.ruleConfiguration(solutionId);
    }
  );

  const [handleDisableClick] = useStatefulApi(
    () =>
      pepApiRequest({
        method: RequestMethod.Put,
        url: generateApiPath(API_ENABLE_RULE_CONFIGURATION, { solutionId }),
        params: { enabled: false, ruleIds: itemIds },
      }),
    () => {
      showAlert({
        type: AlertType.Info,
        title: 'Rule is Disabled',
        text: 'Rule has been disabled successfully',
      });
      actions.ruleConfiguration(solutionId);
    }
  );

  return (
    <DataTableContext {...search}>
      <DataTableWrapper
        searchBar={false}
        actionsPanel={
          <Box>
            <DataTableSearchBar style={{ paddingRight: '10px' }} />
            <DataTableWrapperActionButton
              startIcon={<Add />}
              onClick={showNewRuleConfigurationDialog}
              disabled={isPlatformReader}
            >
              Create New
            </DataTableWrapperActionButton>
            {item.length >= 1 && (
              <MoreActionsMenu
                onDeleteClick={showDeleteDialog}
                onDisableClick={handleDisableClick}
                onEnableClick={handleEnableClick}
                canEnable={canEnable}
                canDisable={canDisable}
              />
            )}
          </Box>
        }
      >
        <DataTable
          data={data}
          loading={loading}
          columns={columns}
          checkboxes={true}
          onSelectItems={onSelectItems}
          {...paging}
          {...sorting}
        />
        {CreateNewRuleConfigurationDialog}
        {DeleteDialog}
      </DataTableWrapper>
    </DataTableContext>
  );
}
